import Vue from 'vue';
import { ActionTree, MutationTree, Module } from 'vuex';
import { GettersConfig } from '@/interfaces/store';

export type QueueType = 'pageViews' | 'events' | 'consents';
type SessionEnvironmentOrigin = 'seo' | 'organic' | 'social' | 'search' | 'direct' | 'redirect';
export type PageEntryMode = 'modal';
export type OverridePageState = {
	pageType: string;
	pageViewUUID: string;
	pageEntryMode?: 'modal';
} | null;

export type SessionEnvironment = {
	sessionReady: boolean;
	origin: SessionEnvironmentOrigin | null;
	hasAdBlocker: boolean | null;
	originPageUrl: string | null;
	originPageReferred: string | null;
	originRefrMedium: string | null;
	originRefrSource: string | null;
	originRefrSocial: string | null;
	env: string | null;
};

export type TrackingQueues = Record<QueueType, any[]>;

export type TrackingState = {
	pagesVisited: number;
	queues: TrackingQueues;
	sessionEnvironment: SessionEnvironment;
	pageViewUUID: string | null;
	overridePageState: OverridePageState;
};
export type TrackingGetters = {
	getQueueOf: (queueType: QueueType) => any[];
};

export type TrackingGettersConfig = GettersConfig<TrackingState, TrackingGetters>;

export type TrackingStore = {
	state: TrackingState;
	getters: TrackingGettersConfig;
};

// STATE
export const getInitialTrackingState = (): TrackingState => ({
	// SSR SR Campaigns rely on pagesVisted for first page view detection
	pagesVisited: 0,
	queues: {
		pageViews: [],
		events: [],
		consents: [],
	} as unknown as TrackingQueues,
	sessionEnvironment: {
		sessionReady: false,
		origin: null,
		env: null,
		hasAdBlocker: null,
		originPageUrl: null,
		originPageReferred: null,
		originRefrMedium: null,
		originRefrSource: null,
		originRefrSocial: null,
	},
	pageViewUUID: null,
	overridePageState: null,
});

// GETTERS
const getters: TrackingGettersConfig = {
	getQueueOf: state => (queueType: QueueType) => {
		return state.queues[queueType];
	},
};

// ACTIONS
const actions: ActionTree<TrackingState, any> = {
	setQueues: async ({ commit }, queues) => {
		commit('SET_QUEUES', queues);
	},

	resetQueue: async ({ commit }, queueType: QueueType) => {
		commit('RESET_QUEUE', queueType);
	},

	addToQueue: async ({ commit }, { queueType, event }) => {
		commit('ADD_TO_QUEUES', { queueType, event });
	},

	addPageView: async ({ dispatch }, event) => {
		const queueType = 'pageViews';
		dispatch('addToQueue', { queueType, event });
	},

	addTrackEvent: async ({ dispatch }, event) => {
		const queueType = 'events';
		dispatch('addToQueue', { queueType, event });
	},

	setPageViewUUID: ({ commit }, pageViewUUID: string | null) => {
		commit('UPDATE_PAGE_VIEW_UUID', pageViewUUID);
	},

	setSessionEnvironment: async ({ commit }, sessionEnvironment: Partial<SessionEnvironment>) => {
		commit('SET_SESSION_ENVIRONMENT', sessionEnvironment);
	},

	setSessionEnvironmentByKey: async ({ commit }, { key, value }) => {
		commit('SET_SESSION_ENVIRONMENT_KEY', { key, value });
	},

	updateQueuedEventsContext: async ({ commit }, { queueType, context }) => {
		commit('ADD_CONTEXT_TO_QUEUE', { queueType, context });
	},

	setOverridePageState: ({ commit }, pageState: OverridePageState) => {
		commit('SET_OVERRIDE_PAGE_STATE', pageState);
	},
};

// MUTATIONS
const mutations: MutationTree<TrackingState> = {
	RESET_QUEUE: (state, queueType: QueueType) => {
		Vue.set(state.queues, `${queueType}`, []);
	},

	SET_QUEUES: (state, queues: TrackingQueues) => {
		state.queues = queues;
	},

	ADD_TO_QUEUES: (state, { queueType, event }: { queueType: QueueType; event: any }) => {
		state.queues[queueType].push(event);
	},

	INCREASE_PAGES_VISITED: state => {
		state.pagesVisited = state.pagesVisited + 1;
	},

	SET_SESSION_ENVIRONMENT: (state, sessionEnvironment: Partial<SessionEnvironment>) => {
		Vue.set(state, 'sessionEnvironment', { ...state.sessionEnvironment, ...sessionEnvironment });
	},

	UPDATE_PAGE_VIEW_UUID: (state, pageViewUUID: string | null) => {
		state.pageViewUUID = pageViewUUID;
	},

	SET_SESSION_ENVIRONMENT_KEY: <S extends keyof SessionEnvironment, V extends SessionEnvironment[S]>(
		state: TrackingState,
		{ key, value }: { key: S; value: V }
	) => {
		Vue.set(state.sessionEnvironment, key, value);
	},

	SET_OVERRIDE_PAGE_STATE: (state, pageState: OverridePageState) => {
		state.overridePageState = pageState;
	},
};

export default {
	namespaced: true,
	state: getInitialTrackingState,
	getters,
	actions,
	mutations,
} as Module<TrackingState, any>;
